import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp, TrendingDown, FileText, CreditCard, ArrowUpRight,
  Wallet, FileInput, FileOutput, Eye, CheckCircle2, Send, Clock,
  CircleAlert, Ban, CircleDot, Receipt, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatCurrency = (val) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border/50 rounded-lg px-4 py-3 shadow-xl">
        <p className="text-xs text-muted-foreground font-mono-data mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
            {formatCurrency(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function WorkflowStep({ icon: Icon, label, count, color, isLast }) {
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <div className={`flex flex-col items-center gap-1 min-w-[56px] sm:min-w-[80px]`}>
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <span className="font-mono-data text-base sm:text-lg font-bold">{count}</span>
        <span className="text-[10px] sm:text-[11px] text-muted-foreground text-center leading-tight">{label}</span>
      </div>
      {!isLast && <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-border/60 flex-shrink-0 mt-[-24px]" />}
    </div>
  );
}

export default function DashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [kpiRes, wfRes] = await Promise.all([
          dashboardAPI.getKPIs(),
          dashboardAPI.getWorkflow(),
        ]);
        setKpis(kpiRes.data);
        setWorkflow(wfRes.data);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-card rounded-xl border border-border/30" />)}
        </div>
        <div className="h-80 bg-card rounded-xl border border-border/30" />
      </div>
    );
  }

  const kpiCards = [
    { title: 'Umsatz', value: formatCurrency(kpis?.total_revenue), icon: TrendingUp, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
    { title: 'Ausgaben', value: formatCurrency(kpis?.total_costs), icon: TrendingDown, color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20' },
    { title: 'Gewinn', value: formatCurrency(kpis?.profit), icon: Wallet, color: 'text-primary', bgColor: 'bg-primary/10', borderColor: 'border-primary/20' },
    { title: 'Offene Rechnungen', value: kpis?.open_invoices || 0, subtitle: `${kpis?.paid_invoices || 0} bezahlt`, icon: FileText, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
  ];

  const inc = workflow?.incoming || {};
  const out = workflow?.outgoing || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Dashboard</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Überblick über Ihre Finanzkennzahlen</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger-children" data-testid="kpi-cards">
        {kpiCards.map((kpi, idx) => (
          <Card key={idx} className={`bg-card border ${kpi.borderColor} hover:border-primary/30 transition-colors duration-300`} data-testid={`kpi-card-${idx}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg ${kpi.bgColor} flex items-center justify-center`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{kpi.title}</span>
              </div>
              <div className="font-mono-data text-xl sm:text-2xl font-bold mb-1" data-testid={`kpi-value-${idx}`}>{kpi.value}</div>
              {kpi.subtitle && <p className="text-xs text-muted-foreground">{kpi.subtitle}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Workflow Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="workflow-section">
        {/* Incoming Pipeline */}
        <Card className="bg-card border border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <FileInput className="w-4 h-4 text-blue-400" />
              Eingangsrechnungen
              <Badge variant="outline" className="ml-auto text-xs font-mono-data">{inc.total || 0} gesamt</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-center gap-2 py-2">
              <WorkflowStep icon={Eye} label="Prüfung" count={inc.review || 0} color="bg-amber-500/15 text-amber-400" />
              <WorkflowStep icon={CheckCircle2} label="Bestätigt" count={inc.confirmed || 0} color="bg-emerald-500/15 text-emerald-400" isLast />
            </div>
          </CardContent>
        </Card>

        {/* Outgoing Pipeline */}
        <Card className="bg-card border border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <FileOutput className="w-4 h-4 text-primary" />
              Ausgangsrechnungen
              <Badge variant="outline" className="ml-auto text-xs font-mono-data">{out.total || 0} gesamt</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-center gap-1 py-2 flex-wrap">
              <WorkflowStep icon={FileText} label="Entwurf" count={out.draft || 0} color="bg-slate-500/15 text-slate-400" />
              <WorkflowStep icon={CircleDot} label="Offen" count={out.open || 0} color="bg-blue-500/15 text-blue-400" />
              <WorkflowStep icon={Send} label="Versendet" count={out.sent || 0} color="bg-indigo-500/15 text-indigo-400" />
              <WorkflowStep icon={CheckCircle2} label="Bezahlt" count={out.paid || 0} color="bg-emerald-500/15 text-emerald-400" />
              <WorkflowStep icon={CircleAlert} label="Überfällig" count={out.overdue || 0} color="bg-red-500/15 text-red-400" />
              <WorkflowStep icon={Ban} label="Storniert" count={out.cancelled || 0} color="bg-neutral-500/15 text-neutral-400" isLast />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts + UStVA */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Revenue Chart */}
        <Card className="lg:col-span-8 bg-card border border-border/50" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-primary" />
              Umsatzentwicklung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={kpis?.monthly_data || []}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00E5FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217, 33%, 17%)" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} fontFamily="JetBrains Mono" />
                  <YAxis stroke="#94A3B8" fontSize={12} fontFamily="JetBrains Mono" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#00E5FF" strokeWidth={2} fill="url(#revenueGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* UStVA Quick Card + Summary */}
        <div className="lg:col-span-4 space-y-4">
          {/* UStVA Quick View */}
          <Card
            className="bg-card border border-border/50 cursor-pointer hover:border-primary/40 transition-colors"
            onClick={() => navigate('/umsatzsteuer')}
            data-testid="ustva-quick-card"
          >
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Receipt className="w-4 h-4 text-amber-400" />
                UStVA – Aktueller Monat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-sm text-muted-foreground">Umsatzsteuer</span>
                <span className="font-mono-data font-bold text-red-400">{formatCurrency(kpis?.current_month_ust)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-sm text-muted-foreground">Vorsteuer</span>
                <span className="font-mono-data font-bold text-emerald-400">{formatCurrency(kpis?.current_month_vst)}</span>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-sm font-semibold">Zahllast</span>
                <span className={`font-mono-data text-lg font-bold ${(kpis?.current_month_zahllast || 0) >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {formatCurrency(kpis?.current_month_zahllast)}
                </span>
              </div>
              <p className="text-xs text-primary text-center pt-1">Details anzeigen →</p>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card className="bg-card border border-border/50" data-testid="summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-secondary" />
                Zusammenfassung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-sm text-muted-foreground">Gesamtrechnungen</span>
                <span className="font-mono-data font-bold">{kpis?.invoice_count || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-sm text-muted-foreground">Bezahlt</span>
                <span className="font-mono-data font-bold text-emerald-400">{kpis?.paid_invoices || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-sm text-muted-foreground">Offen</span>
                <span className="font-mono-data font-bold text-amber-400">{kpis?.open_invoices || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-muted-foreground">Cashflow</span>
                <span className={`font-mono-data font-bold ${(kpis?.cashflow || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(kpis?.cashflow)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
